/**
 * Chart theming utilities for recharts.
 *
 * All colors resolve from the CSS variables that drive the dark/light glass UI,
 * so charts automatically respect the active theme without extra configuration.
 *
 * Per-user series colors use a fixed accent palette that contrasts well on both
 * light and dark muted surfaces.
 */

/** Read a resolved design-token value from the document root at runtime. */
function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/** Semantic chart colors derived from the design-system CSS variables. */
export function getChartColors() {
  return {
    grid: cssVar('--color-border', '#d5dde4'),
    axis: cssVar('--color-foreground', '#36455a'),
    tooltip: {
      bg: cssVar('--color-background', '#eff2f5'),
      border: cssVar('--color-border', '#d5dde4'),
      text: cssVar('--color-foreground', '#36455a'),
      muted: cssVar('--color-muted-foreground', '#64748b'),
    },
    primary: cssVar('--color-primary', '#2e6be6'),
  };
}

/**
 * Fixed per-user accent palette. These are chosen to be visually distinct on
 * both light and dark backgrounds and remain accessible at ~40 % opacity fills.
 *
 * Call `getUserColor(index)` to assign a stable color to each user by their
 * position in a sorted users array.
 */
const USER_ACCENT_PALETTE = [
  '#60a5fa', // blue-400
  '#34d399', // emerald-400
  '#f472b6', // pink-400
  '#fb923c', // orange-400
  '#a78bfa', // violet-400
  '#fbbf24', // amber-400
  '#2dd4bf', // teal-400
  '#f87171', // red-400
  '#818cf8', // indigo-400
  '#4ade80', // green-400
];

export function getUserColor(index: number): string {
  return USER_ACCENT_PALETTE[index % USER_ACCENT_PALETTE.length] ?? '#60a5fa';
}

/** recharts `<CartesianGrid>` props */
export const cartesianGridProps = {
  strokeDasharray: '3 3',
  strokeOpacity: 0.3,
} as const;

/** recharts custom tooltip container style (applied inline). */
export function tooltipContainerStyle(): React.CSSProperties {
  const c = getChartColors();
  return {
    backgroundColor: c.tooltip.bg,
    border: `1px solid ${c.tooltip.border}`,
    borderRadius: '8px',
    fontSize: '12px',
    color: c.tooltip.text,
    boxShadow: 'none',
  };
}
